import React from "react";
import PropTypes from "prop-types";
import {
  AAccordion,
  AAccordionBody,
  AAccordionHeader,
  AAccordionHeaderTitle,
  AAccordionPanel
} from "@cisco-sbg-ui/magna-react";
import classNames from "classnames";
import DrawerContent from "../DrawerContent/DrawerContent";
import css from "./CollapsibleDrawerContent.module.scss";

/**
 * Used to wrap collapsible content inside of drawers. The state of
 * the component can be controlled or uncontrolled. In the case of a
 * controlled component, pass the `collapsed` prop in combination
 * with the `onToggle` prop to control the `collapsed` state yourself.
 *
 * For an uncontrolled component, you can still set an initial
 * `collapsed` state by passing `defaultCollapsed`, but any
 * updates to this prop after the initial mount will be ignored.
 *
 * `CollapsibleDrawerContent` also inherits all props from the
 * `<DrawerContent />` component.
 */
const CollapsibleDrawerContent = ({
  className,
  children,
  defaultCollapsed = false,
  collapsed,
  hideDivider = false,
  title,
  titleIcon,
  onToggle,
  ...rest
}) => {
  const isControlled = typeof collapsed === "boolean";
  const accordionProps = {};
  if (isControlled) {
    accordionProps.collapsed = collapsed;
    accordionProps.onToggle = onToggle;
    accordionProps.panelKey = "collapsible-drawer-content";
  } else {
    accordionProps.collapsed = defaultCollapsed;
  }
  return (
    <DrawerContent hideDivider={hideDivider} className="pa-0" {...rest}>
      <AAccordion
        className={classNames(css.collapsibleDrawerContent, className)}
        bordered={false}>
        <AAccordionPanel {...accordionProps}>
          <AAccordionHeader>
            <AAccordionHeaderTitle className={css.title}>
              <span className={css.titleText}>{title}</span>
              {titleIcon && <span className={css.titleIcon}>{titleIcon}</span>}
            </AAccordionHeaderTitle>
          </AAccordionHeader>
          <AAccordionBody>{children}</AAccordionBody>
        </AAccordionPanel>
      </AAccordion>
    </DrawerContent>
  );
};

CollapsibleDrawerContent.propTypes = {
  /**
   * Optional className to apply to the component.
   */
  className: PropTypes.string,

  /**
   * Used to set the `collapsed` state for controlled versions of
   * this component. If you don't want to manage the `collapsed`
   * state yourself, consider rendering this component as uncontrolled
   * in combination with the `defaultCollapsed` prop.
   */
  collapsed: PropTypes.bool,

  /**
   * Used for the _initial_ state of an uncontrolled version of
   * this component. Any updates to this prop will be ignored after
   * the component is mounted.
   *
   * If you are looking for controlled behavior, use the `collapsed`
   * prop in combination with the `onToggle` prop instead.
   */
  defaultCollapsed: PropTypes.bool,

  /**
   * Determines if the drawer section should render with a divider
   * at the bottom of the component.
   */
  hideDivider: PropTypes.bool,

  /**
   * Used for controlled versions of this component to handle the user clicking
   * the accordion toggle of this component.
   */
  onToggle: PropTypes.func,

  /**
   * The title of the accordion
   */
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,

  // An icon to display alongside the accordion's title.
  titleIcon: PropTypes.object
};

export default CollapsibleDrawerContent;
